/**
 * Reconciliation Service — BE-13 (`ReconciliationController`, /api/reconciliations). Real backend.
 * Returns `{ success, data }` with camelCase data, the shape the pages use.
 * Amounts are signed in the account's natural direction; every session call returns the whole session with the
 * server's totals. An adjustment is the TC admin's.
 */

import ApiService from '../api.service';
import { keysCamelToSnake, unwrapData, unwrapList } from '../../utils/apiConvert';

const BASE = '/api/reconciliations';
const ok = (data) => ({ success: true, data });
const clean = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => v !== '' && v !== undefined && v !== null));

export const reconciliationService = {
  // The hub: every active bank, cash and wallet account and card (empty until the ledger has started)
  getAccountStatus: async () => ok(unwrapList(await ApiService.get(`${BASE}/accounts`)).items),

  // params: { accountType, from, to } — completed and in-progress sessions, newest statement first
  getHistory: async (params) => ok(unwrapList(await ApiService.get(BASE, { params: clean(params) })).items),

  // data: { accountType, accountId, statementDate, statementBalance, notes }
  startReconciliation: async (data) => ok(unwrapData(await ApiService.post(BASE, keysCamelToSnake(clean(data))))),

  getReconciliation: async (id) => ok(unwrapData(await ApiService.get(`${BASE}/${id}`))),

  // The full list of ticked ledger line ids — replaces the earlier selection
  matchItems: async (id, itemIds) => ok(unwrapData(await ApiService.put(`${BASE}/${id}/match`, { item_ids: itemIds }))),

  // data: { amount (signed), reason, counterAccountId (optional) }
  addAdjustment: async (id, data) => ok(unwrapData(await ApiService.post(`${BASE}/${id}/adjustments`, keysCamelToSnake(clean(data))))),

  completeReconciliation: async (id) => ok(unwrapData(await ApiService.post(`${BASE}/${id}/complete`))),

  cancelReconciliation: async (id) => ok(unwrapData(await ApiService.post(`${BASE}/${id}/cancel`))),

  // Counter accounts for an adjustment: the chart, with manualAllowed per account
  getGlAccounts: async () => ok(unwrapList(await ApiService.get('/api/gl-accounts')).items),

  // id → name of the typing center's users, for "Reconciled By"
  getUserNames: async (typingCenterId) => {
    if (!typingCenterId) return {};
    const res = await ApiService.get(`/api/users/typing-center/${typingCenterId}`);
    return Object.fromEntries((res?.data?.data || []).map((u) => [u.id, u.full_name || u.fullName || u.username]));
  },
};

export default reconciliationService;
